import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import zxcvbn from 'zxcvbn';
import { MapPin, Building, Lock, User, Mail, Phone, Plus, Edit, Trash2, Camera, Upload, DollarSign, Shield, Sparkles, UserCircle, ChevronRight, Globe, Bot, Settings, Download, AlertTriangle, FileText, CheckCircle2, XCircle, BellRing, Users, Package, FileDown, ExternalLink } from 'lucide-react';

import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Separator } from '../components/ui/separator';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { cn } from '../lib/utils';

import { profileAPI } from '../lib/api';
import api from '../lib/api';
import { CURRENCY_SYMBOLS } from '../utils/currency';

// Sub-page components embedded as Profile tabs
import UsersPage from './hr/Users';

const INDUSTRY_OPTIONS = [
  'Real Estate', 'SaaS & B2B Tech', 'IT Services & Consulting', 'Healthcare',
  'Pharma & Life Sciences', 'Education & EdTech', 'Manufacturing & Industrial',
  'Automotive', 'Banking & Insurance (BFSI)', 'Fintech', 'D2C & Retail',
  'E-commerce & Marketplaces', 'Construction & Interior', 'Travel & Hospitality',
  'Food & Beverage', 'Logistics & Supply Chain', 'Legal & Professional Services',
  'Marketing & Advertising', 'Media & Entertainment', 'Telecom',
  'Energy & Utilities', 'Agriculture & AgriTech', 'Testing & Certification',
  'Government & Public Sector', 'Non-Profit & NGO', 'HR & Staffing',
  'Fitness & Wellness', 'Beauty & Personal Care', 'Other',
];
import ProductsCatalog from './settings/ProductsCatalog';
import DataImport from './DataImportPage';

const POPULAR_CURRENCIES = [
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ' },
  { code: 'SAR', name: 'Saudi Riyal', symbol: '﷼' },
  { code: 'MXN', name: 'Mexican Peso', symbol: 'MX$' },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R' },
  { code: 'KRW', name: 'South Korean Won', symbol: '₩' },
  { code: 'THB', name: 'Thai Baht', symbol: '฿' },
  { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM' },
  { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp' },
  { code: 'PHP', name: 'Philippine Peso', symbol: '₱' },
];

const SIDEBAR_SECTIONS = [
  {
    category: 'Profile',
    items: [
      { id: 'my-profile', label: 'My Profile', icon: User },
      { id: 'password-security', label: 'Password & Security', icon: Lock },
    ],
  },
  {
    category: 'Organization',
    items: [
      { id: 'company-details', label: 'Company Details', icon: Building },
      { id: 'company-addresses', label: 'Company Addresses', icon: MapPin },
    ],
  },
  {
    category: 'Team & Catalog',
    items: [
      { id: 'users', label: 'Users', icon: Users },
      { id: 'products', label: 'Products & Services', icon: Package },
    ],
  },
  {
    category: 'Currency & Locale',
    items: [
      { id: 'workspace', label: 'Workspace', icon: Globe },
      { id: 'ai-settings', label: 'AI Settings', icon: Bot },
    ],
  },
  {
    category: 'Privacy & Data',
    items: [
      { id: 'privacy-data', label: 'Privacy & Data', icon: Shield },
      { id: 'data-import', label: 'Data Import', icon: FileDown },
    ],
  },
];

export default function Profile() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const [activeSection, setActiveSection] = useState(searchParams.get('tab') || 'my-profile');

  // Keep the section in sync with the URL (?tab=...) so every settings tab has
  // its own shareable link and browser back/forward works.
  useEffect(() => {
    const t = searchParams.get('tab') || 'my-profile';
    setActiveSection((prev) => (prev === t ? prev : t));
  }, [searchParams]);

  // State
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Organization details state
  const [orgDetails, setOrgDetails] = useState({ company_name: '', company_size: '', industry: '', website_url: '' });
  const [editingOrgDetails, setEditingOrgDetails] = useState(false);
  const [orgDetailsForm, setOrgDetailsForm] = useState({ company_name: '', company_size: '', industry: '', website_url: '' });
  const [savingOrgDetails, setSavingOrgDetails] = useState(false);

  // Profile image state
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Personal info editing
  const [editingPersonalInfo, setEditingPersonalInfo] = useState(false);
  const [personalInfoForm, setPersonalInfoForm] = useState({ name: '' });

  // Currency selection
  const [changingCurrency, setChangingCurrency] = useState(false);
  const [invoiceTaxForm, setInvoiceTaxForm] = useState({ company_name: '', gstin: '', company_logo: '' });
  const [savingInvoiceTax, setSavingInvoiceTax] = useState(false);

  // Address sheets
  const [addressSheet, setAddressSheet] = useState({ open: false, type: '', data: null });
  const [addressForm, setAddressForm] = useState({
    street: '', city: '', state: '', postal_code: '', country: '', landmark: '', label: ''
  });

  // Password change
  const [passwordSheet, setPasswordSheet] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '', new_password: '', confirm_password: ''
  });
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, feedback: {} });
  const [passwordError, setPasswordError] = useState('');

  // Delete confirmation
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: '', id: null });

  // AI Preferences
  const [aiPrefs, setAiPrefs] = useState(null);
  const [aiPrefsLoading, setAiPrefsLoading] = useState(false);
  const [aiPrefsSaving, setAiPrefsSaving] = useState(false);

  // Privacy & Data
  const [privacyStatus, setPrivacyStatus] = useState(null);
  const [privacyLoading, setPrivacyLoading] = useState(false);
  const [deleteAccountDialog, setDeleteAccountDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [exportingJson, setExportingJson] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);

  // ---------- Data Loading ----------
  useEffect(() => {
    loadProfile();
    loadOrgDetails();
    loadAIPreferences();
    loadPrivacyStatus();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await profileAPI.get();
      setUser(response.data);
      setPersonalInfoForm({ name: response.data.name });
      setInvoiceTaxForm({ company_name: response.data.company_name || '', gstin: response.data.gstin || '', company_logo: response.data.company_logo || '' });
      if (response.data.profile_image) {
        setProfileImagePreview(response.data.profile_image);
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const loadOrgDetails = async () => {
    try {
      const response = await api.get('/api/onboarding/status');
      const data = response.data;
      const details = {
        company_name: data.company_name || '',
        company_size: data.company_size || '',
        industry: data.industry || '',
        website_url: data.website_url || ''
      };
      setOrgDetails(details);
      setOrgDetailsForm(details);
    } catch (error) {
      console.error('Failed to load organization details:', error);
    }
  };

  const loadAIPreferences = async () => {
    setAiPrefsLoading(true);
    try {
      const res = await profileAPI.getAIPreferences();
      setAiPrefs(res.data);
    } catch {
      console.error('Failed to load AI preferences');
    } finally {
      setAiPrefsLoading(false);
    }
  };

  const saveAIPreference = async (key, value) => {
    setAiPrefsSaving(true);
    try {
      const res = await profileAPI.updateAIPreferences({ [key]: value });
      setAiPrefs(res.data);
      toast.success('AI preference saved');
    } catch {
      toast.error('Failed to save AI preference');
    } finally {
      setAiPrefsSaving(false);
    }
  };

  // Privacy & Data handlers
  const loadPrivacyStatus = async () => {
    setPrivacyLoading(true);
    try {
      const res = await profileAPI.getPrivacyStatus();
      setPrivacyStatus(res.data);
    } catch {
      console.error('Failed to load privacy status');
    } finally {
      setPrivacyLoading(false);
    }
  };

  const handleAcceptPrivacy = async () => {
    try {
      await profileAPI.acceptPrivacy();
      toast.success('Privacy policy accepted');
      await loadPrivacyStatus();
    } catch {
      toast.error('Failed to accept privacy policy');
    }
  };

  const handleRequestDeletion = async () => {
    if (!deletePassword) { toast.error('Password is required'); return; }
    setDeletingAccount(true);
    try {
      const res = await profileAPI.requestDeletion({ password: deletePassword, reason: deleteReason || undefined });
      toast.success(res.data.message);
      setDeleteAccountDialog(false);
      setDeletePassword('');
      setDeleteReason('');
      await loadPrivacyStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request deletion');
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleCancelDeletion = async () => {
    try {
      const res = await profileAPI.cancelDeletion();
      toast.success(res.data.message);
      await loadPrivacyStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel deletion');
    }
  };

  const handleExportJson = async () => {
    setExportingJson(true);
    try {
      const res = await profileAPI.exportDataJson();
      const blob = new Blob([res.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lockene_data_export_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Data exported as JSON');
    } catch {
      toast.error('Failed to export data');
    } finally {
      setExportingJson(false);
    }
  };

  const handleExportCsv = async () => {
    setExportingCsv(true);
    try {
      const res = await profileAPI.exportDataCsv();
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lockene_data_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Data exported as CSV');
    } catch {
      toast.error('Failed to export data');
    } finally {
      setExportingCsv(false);
    }
  };

  const handleMarketingToggle = async (key, value) => {
    try {
      await profileAPI.updateMarketingConsent({ [key]: value });
      setPrivacyStatus(prev => ({
        ...prev,
        marketing_consent: { ...prev.marketing_consent, [key]: value },
      }));
      toast.success('Email preference updated');
    } catch {
      toast.error('Failed to update preference');
    }
  };

  // ---------- Handlers (unchanged) ----------
  const handleSaveOrgDetails = async () => {
    try {
      setSavingOrgDetails(true);
      await api.put('/api/onboarding/organization', orgDetailsForm);
      setOrgDetails(orgDetailsForm);
      setEditingOrgDetails(false);
      toast.success('Organization details updated');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Failed to update organization details');
    } finally {
      setSavingOrgDetails(false);
    }
  };

  const handleSaveInvoiceTax = async () => {
    try {
      setSavingInvoiceTax(true);
      const res = await profileAPI.update({
        company_name: invoiceTaxForm.company_name,
        gstin: invoiceTaxForm.gstin,
        company_logo: invoiceTaxForm.company_logo || '',
      });
      const updated = { ...user, company_name: res.data.company_name, gstin: res.data.gstin, company_logo: res.data.company_logo };
      setUser(updated);
      setInvoiceTaxForm({ company_name: res.data.company_name || '', gstin: res.data.gstin || '', company_logo: res.data.company_logo || '' });
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      storedUser.company_name = res.data.company_name;
      storedUser.gstin = res.data.gstin;
      storedUser.company_logo = res.data.company_logo;
      localStorage.setItem('user', JSON.stringify(storedUser));
      toast.success('Invoice & tax details saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save invoice details');
    } finally {
      setSavingInvoiceTax(false);
    }
  };

  const handleLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 400 * 1024) { toast.error('Logo must be under 400KB'); return; }
    const reader = new FileReader();
    reader.onload = () => setInvoiceTaxForm((prev) => ({ ...prev, company_logo: reader.result }));
    reader.readAsDataURL(file);
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 3 * 1024 * 1024) { toast.error('Image size must be less than 3MB'); return; }
    const previewUrl = URL.createObjectURL(file);
    setProfileImagePreview(previewUrl);
    try {
      setUploadingImage(true);
      const response = await profileAPI.uploadImage(file);
      toast.success('Profile image updated successfully');
      const updatedImageUrl = response.data.image_url;
      setProfileImagePreview(updatedImageUrl);
      setUser({ ...user, profile_image: updatedImageUrl });
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      storedUser.profile_image = updatedImageUrl;
      localStorage.setItem('user', JSON.stringify(storedUser));
      window.dispatchEvent(new Event('storage'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
      setProfileImagePreview(user?.profile_image || null);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleUpdatePersonalInfo = async (fieldData) => {
    try {
      const data = fieldData || personalInfoForm;
      await profileAPI.update(data);
      toast.success('Saved');
      await loadProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleCurrencyChange = async (newCurrency) => {
    try {
      setChangingCurrency(true);
      await profileAPI.update({ currency: newCurrency });
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      storedUser.currency = newCurrency;
      localStorage.setItem('user', JSON.stringify(storedUser));
      toast.success('Currency updated successfully');
      await loadProfile();
      window.location.reload();
    } catch (error) {
      let errorMessage = 'Failed to update currency';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') errorMessage = error.response.data.detail;
        else if (Array.isArray(error.response.data.detail)) errorMessage = error.response.data.detail.map(err => err.msg || err).join(', ');
      }
      toast.error(errorMessage);
    } finally {
      setChangingCurrency(false);
    }
  };

  const openAddressSheet = (type, data = null) => {
    if (data) setAddressForm({ ...data });
    else setAddressForm({ street: '', city: '', state: '', postal_code: '', country: '', landmark: '', label: '' });
    setAddressSheet({ open: true, type, data });
  };
  const closeAddressSheet = () => {
    setAddressSheet({ open: false, type: '', data: null });
    setAddressForm({ street: '', city: '', state: '', postal_code: '', country: '', landmark: '', label: '' });
  };
  const handleSaveAddress = async () => {
    try {
      const addressData = { type: addressSheet.type, ...addressForm };
      if (addressSheet.data) {
        await profileAPI.updateAddress(addressSheet.data.id || 'personal', addressData);
        toast.success(`${addressSheet.type === 'personal' ? 'Personal' : 'Company'} address updated`);
      } else {
        await profileAPI.addAddress(addressData);
        toast.success(`${addressSheet.type === 'personal' ? 'Personal' : 'Company'} address added`);
      }
      closeAddressSheet();
      await loadProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save address');
    }
  };
  const handleDeleteAddress = async () => {
    try {
      await profileAPI.deleteAddress(deleteDialog.id || 'personal', deleteDialog.type);
      toast.success(`${deleteDialog.type === 'personal' ? 'Personal' : 'Company'} address deleted`);
      setDeleteDialog({ open: false, type: '', id: null });
      await loadProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete address');
    }
  };

  const handlePasswordChange = (field, value) => {
    setPasswordForm({ ...passwordForm, [field]: value });
    if (field === 'new_password') {
      const result = zxcvbn(value);
      setPasswordStrength({ score: result.score, feedback: result.feedback });
    }
    if (field === 'confirm_password' || field === 'new_password') {
      if (passwordForm.new_password !== value && field === 'confirm_password') setPasswordError('Passwords do not match');
      else if (passwordForm.confirm_password !== value && field === 'new_password') setPasswordError('Passwords do not match');
      else setPasswordError('');
    }
  };
  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) { setPasswordError('Passwords do not match'); return; }
    if (passwordStrength.score < 2) { toast.error('Password is too weak.'); return; }
    try {
      await profileAPI.changePassword(passwordForm);
      toast.success('Password changed successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      setPasswordStrength({ score: 0, feedback: {} });
      setPasswordError('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };
  const getPasswordStrengthLabel = (score) => ['Too weak', 'Weak', 'Fair', 'Strong', 'Very strong'][score];
  const getPasswordStrengthColor = (score) => ['hsl(4 82% 60%)', 'hsl(38 92% 50%)', 'hsl(38 92% 50%)', 'hsl(162 60% 44%)', 'hsl(162 60% 44%)'][score];

  const isAdminAccount = () => {
    if (!user) return false;
    if (user.is_superadmin || user.is_org_admin || user.is_platform_admin || user.is_admin) return true;
    const role = user.role?.toLowerCase();
    return ['superadmin', 'platform_admin', 'org_admin', 'admin', 'owner', 'manager'].includes(role);
  };
  const getAccountTypeBadge = () => {
    if (isAdminAccount()) return {
      label: 'Admin Account',
      icon: <Sparkles className="h-3.5 w-3.5 mr-1" />,
      className: 'bg-gradient-to-r from-amber-500/10 to-orange-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20 hover:bg-amber-500/20'
    };
    return {
      label: 'User Account',
      icon: <UserCircle className="h-3.5 w-3.5 mr-1" />,
      className: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700'
    };
  };

  // ---------- Loading / Error states ----------
  if (loading) return (
    <div className="space-y-6 p-6" data-testid="profile-loading-skeleton">
      <div className="flex items-center gap-4">
        <div className="h-16 w-16 rounded-full bg-muted animate-pulse" />
        <div className="space-y-2">
          <div className="h-6 w-40 bg-muted rounded animate-pulse" />
          <div className="h-4 w-56 bg-muted/60 rounded animate-pulse" />
        </div>
      </div>
      <div className="flex gap-2">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-9 w-24 bg-muted/60 rounded animate-pulse" />
        ))}
      </div>
      <div className="rounded-lg border p-6 space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex items-center gap-4">
            <div className="h-4 w-24 bg-muted/60 rounded animate-pulse" />
            <div className="h-9 flex-1 bg-muted/40 rounded animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  );
  if (!user) return <div className="flex items-center justify-center min-h-screen">Failed to load profile</div>;

  // ---------- Section Content Renderers ----------
  const renderMyProfile = () => (
    <div className="space-y-8" data-testid="profile-header">
      {/* Profile Image - hover to edit */}
      <div>
        <p className="text-sm font-bold text-foreground mb-3">Profile Image</p>
        <div className="flex items-center gap-4">
          <div className="relative group cursor-pointer" onClick={() => fileInputRef.current?.click()} data-testid="avatar-hover-wrapper">
            <Avatar className="h-16 w-16" data-testid="profile-avatar">
              {profileImagePreview ? (
                <AvatarImage src={profileImagePreview} alt={user.name} />
              ) : (
                <AvatarFallback className="text-lg bg-muted">{user.name.slice(0, 2).toUpperCase()}</AvatarFallback>
              )}
            </Avatar>
            <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid="avatar-edit-overlay">
              <Camera className="h-5 w-5 text-white" />
            </div>
            {uploadingImage && <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center"><div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /></div>}
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageSelect} data-testid="avatar-file-input" />
          <Badge variant="outline" className={`${getAccountTypeBadge().className} flex items-center text-xs px-2 py-0.5`} data-testid="account-type-badge">
            {getAccountTypeBadge().icon}
            {getAccountTypeBadge().label}
          </Badge>
        </div>
      </div>

      {/* Name - always editable, auto-save on blur */}
      <div data-testid="personal-info-card">
        <p className="text-sm font-bold text-foreground mb-1">Name</p>
        <Input
          id="personal-name"
          className="h-11 max-w-lg"
          value={personalInfoForm.name}
          onChange={(e) => setPersonalInfoForm({ name: e.target.value })}
          onBlur={() => { if (personalInfoForm.name && personalInfoForm.name !== user.name) handleUpdatePersonalInfo({ name: personalInfoForm.name }); }}
          data-testid="personal-name-input"
        />
      </div>

      {/* Email - always editable, auto-save on blur */}
      <div>
        <p className="text-sm font-bold text-foreground mb-1">Email</p>
        <Input id="personal-email" className="h-11 max-w-lg" value={user.email} disabled data-testid="personal-email-input" />
        <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
      </div>

      {/* Phone - always editable, auto-save on blur */}
      <div>
        <p className="text-sm font-bold text-foreground mb-1">Phone</p>
        <Input
          id="personal-phone"
          className="h-11 max-w-lg"
          placeholder="Enter phone number"
          defaultValue={user.phone || ''}
          onBlur={(e) => { if (e.target.value !== (user.phone || '')) handleUpdatePersonalInfo({ phone: e.target.value }); }}
          data-testid="personal-phone-input"
        />
      </div>

      <Separator />

      {/* Personal Address - inline editable */}
      <div data-testid="personal-address-card">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-bold text-foreground">Personal Address</h3>
          {user.personal_address && (
            <Button variant="ghost" size="sm" onClick={() => setDeleteDialog({ open: true, type: 'personal', id: null })} data-testid="personal-address-delete-button"><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
          )}
        </div>
        <p className="text-sm text-muted-foreground mb-4">Your home or personal address</p>
        <div className="space-y-4 max-w-lg">
          <div>
            <p className="text-sm font-bold text-foreground mb-1">Street</p>
            <Input className="h-11" placeholder="Enter street address" defaultValue={user.personal_address?.street || ''} onBlur={(e) => { const v = e.target.value; if (v) { const addr = { ...user.personal_address, street: v, type: 'personal' }; handleSaveAddressInline('personal', addr); } }} data-testid="personal-address-street" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-bold text-foreground mb-1">City</p>
              <Input className="h-11" placeholder="City" defaultValue={user.personal_address?.city || ''} onBlur={(e) => { const v = e.target.value; if (v && user.personal_address?.street) { handleSaveAddressInline('personal', { ...user.personal_address, city: v, type: 'personal' }); } }} data-testid="personal-address-city" />
            </div>
            <div>
              <p className="text-sm font-bold text-foreground mb-1">State</p>
              <Input className="h-11" placeholder="State" defaultValue={user.personal_address?.state || ''} onBlur={(e) => { const v = e.target.value; if (v && user.personal_address?.street) { handleSaveAddressInline('personal', { ...user.personal_address, state: v, type: 'personal' }); } }} data-testid="personal-address-state" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-bold text-foreground mb-1">Postal Code</p>
              <Input className="h-11" placeholder="Postal code" defaultValue={user.personal_address?.postal_code || ''} onBlur={(e) => { const v = e.target.value; if (v && user.personal_address?.street) { handleSaveAddressInline('personal', { ...user.personal_address, postal_code: v, type: 'personal' }); } }} data-testid="personal-address-postal" />
            </div>
            <div>
              <p className="text-sm font-bold text-foreground mb-1">Country</p>
              <Input className="h-11" placeholder="Country" defaultValue={user.personal_address?.country || ''} onBlur={(e) => { const v = e.target.value; if (v && user.personal_address?.street) { handleSaveAddressInline('personal', { ...user.personal_address, country: v, type: 'personal' }); } }} data-testid="personal-address-country" />
            </div>
          </div>
          <div>
            <p className="text-sm font-bold text-foreground mb-1">Landmark <span className="font-normal text-muted-foreground">(optional)</span></p>
            <Input className="h-11" placeholder="Near landmark" defaultValue={user.personal_address?.landmark || ''} onBlur={(e) => { if (user.personal_address?.street) { handleSaveAddressInline('personal', { ...user.personal_address, landmark: e.target.value, type: 'personal' }); } }} data-testid="personal-address-landmark" />
          </div>
        </div>
      </div>
    </div>
  );

  const renderPasswordSecurity = () => (
    <div data-testid="password-change-card">
      <h3 className="text-lg font-bold text-foreground mb-1">Password & Security</h3>
      <p className="text-sm text-muted-foreground mb-6">Manage your password and account security</p>
      <div className="space-y-5 max-w-lg">
        <div>
          <p className="text-sm font-bold text-foreground mb-1">Current Password</p>
          <Input
            type="password"
            placeholder="Enter current password"
            className="h-11"
            value={passwordForm.current_password}
            onChange={(e) => handlePasswordChange('current_password', e.target.value)}
            data-testid="current-password-input"
          />
        </div>
        <div>
          <p className="text-sm font-bold text-foreground mb-1">New Password</p>
          <Input
            type="password"
            placeholder="Enter new password"
            className="h-11"
            value={passwordForm.new_password}
            onChange={(e) => handlePasswordChange('new_password', e.target.value)}
            data-testid="new-password-input"
          />
          {passwordForm.new_password && (
            <div className="mt-2 space-y-1">
              <Progress value={passwordStrength.score * 25} className="h-1.5" style={{ '--progress-color': getPasswordStrengthColor(passwordStrength.score) }} />
              <p className="text-xs" style={{ color: getPasswordStrengthColor(passwordStrength.score) }}>
                {getPasswordStrengthLabel(passwordStrength.score)}
              </p>
            </div>
          )}
        </div>
        <div>
          <p className="text-sm font-bold text-foreground mb-1">Confirm New Password</p>
          <Input
            type="password"
            placeholder="Confirm new password"
            className="h-11"
            value={passwordForm.confirm_password}
            onChange={(e) => handlePasswordChange('confirm_password', e.target.value)}
            data-testid="confirm-password-input"
          />
          {passwordError && <p className="text-xs text-destructive mt-1">{passwordError}</p>}
        </div>
        <Button
          onClick={handleChangePassword}
          disabled={!passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password || passwordStrength.score < 2 || !!passwordError}
          className="w-full h-11"
          data-testid="save-password-button"
        >
          Update Password
        </Button>
      </div>
    </div>
  );

  const handleOrgFieldChange = async (field, value) => {
    const updated = { ...orgDetailsForm, [field]: value };
    setOrgDetailsForm(updated);
    try {
      await api.put('/api/onboarding/organization', updated);
      setOrgDetails(updated);
      toast.success('Saved');
    } catch { toast.error('Failed to save'); }
  };

  const handleSaveAddressInline = async (type, addressData) => {
    try {
      const data = { type, ...addressData };
      if (addressData.id || type === 'personal') {
        await profileAPI.updateAddress(addressData.id || 'personal', data);
      } else {
        await profileAPI.addAddress(data);
      }
      toast.success('Address saved');
      await loadProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save address');
    }
  };

  const renderCompanyDetails = () => (
    <div data-testid="organization-details-card">
      <h3 className="text-lg font-bold text-foreground mb-1">Company Details</h3>
      <p className="text-sm text-muted-foreground mb-6">Your company information</p>
      <div className="space-y-6 max-w-lg">
        <div>
          <p className="text-sm font-bold text-foreground mb-1">Company Name</p>
          <Input
            className="h-11"
            placeholder="Enter company name"
            value={orgDetailsForm.company_name || ''}
            onChange={(e) => setOrgDetailsForm({ ...orgDetailsForm, company_name: e.target.value })}
            onBlur={(e) => { if (e.target.value !== orgDetails.company_name) handleOrgFieldChange('company_name', e.target.value); }}
            data-testid="org-company-name-input"
          />
        </div>
        <div>
          <p className="text-sm font-bold text-foreground mb-1">Company Website</p>
          <Input
            className="h-11"
            placeholder="https://example.com"
            value={orgDetailsForm.website_url || ''}
            onChange={(e) => setOrgDetailsForm({ ...orgDetailsForm, website_url: e.target.value })}
            onBlur={(e) => { if (e.target.value !== orgDetails.website_url) handleOrgFieldChange('website_url', e.target.value); }}
            data-testid="org-company-website-input"
          />
        </div>
        <div>
          <p className="text-sm font-bold text-foreground mb-1">Company Size</p>
          <Select value={orgDetailsForm.company_size || ''} onValueChange={(v) => handleOrgFieldChange('company_size', v)}>
            <SelectTrigger className="h-11" data-testid="org-company-size-select"><SelectValue placeholder="Select company size" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0-10">0-10 employees</SelectItem>
              <SelectItem value="11-50">11-50 employees</SelectItem>
              <SelectItem value="51-200">51-200 employees</SelectItem>
              <SelectItem value="201-500">201-500 employees</SelectItem>
              <SelectItem value="500+">500+ employees</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <p className="text-sm font-bold text-foreground mb-1">Industry</p>
          <Select value={orgDetailsForm.industry || ''} onValueChange={(v) => handleOrgFieldChange('industry', v)}>
            <SelectTrigger className="h-11" data-testid="org-industry-select"><SelectValue placeholder="Select industry" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {orgDetailsForm.industry && !INDUSTRY_OPTIONS.includes(orgDetailsForm.industry) && (
                <SelectItem value={orgDetailsForm.industry}>{orgDetailsForm.industry}</SelectItem>
              )}
              {INDUSTRY_OPTIONS.map(i => (
                <SelectItem key={i} value={i}>{i}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {orgDetailsForm.industry === 'Other' && (
            <Input
              className="h-11 mt-2"
              placeholder="Type your industry and click away to save…"
              onBlur={(e) => { const v = e.target.value.trim(); if (v) handleOrgFieldChange('industry', v); }}
              data-testid="org-industry-custom-input"
            />
          )}
        </div>
      </div>
    </div>
  );

  const renderCompanyAddresses = () => (
    <div data-testid="company-addresses-card">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-lg font-bold text-foreground">Company Addresses</h3>
        <Button variant="outline" size="sm" onClick={() => openAddressSheet('company')} data-testid="company-address-add-button"><Plus className="h-4 w-4 mr-2" />Add</Button>
      </div>
      <p className="text-sm text-muted-foreground mb-6">Billing, shipping, and warehouse locations</p>
      {user.company_addresses?.length > 0 ? (
        <div className="space-y-8 max-w-lg">
          {user.company_addresses.map((address) => (
            <div key={address.id} className="space-y-4 pb-6 border-b last:border-b-0" data-testid={`company-address-card-${address.id}`}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-foreground">{address.label || 'Address'}</p>
                <Button variant="ghost" size="sm" onClick={() => setDeleteDialog({ open: true, type: 'company', id: address.id })} data-testid={`company-address-delete-button-${address.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
              </div>
              <div>
                <p className="text-sm font-bold text-foreground mb-1">Label</p>
                <Input className="h-11" defaultValue={address.label || ''} placeholder="e.g. Head Office" onBlur={(e) => handleSaveAddressInline('company', { ...address, label: e.target.value, type: 'company' })} data-testid={`company-address-label-${address.id}`} />
              </div>
              <div>
                <p className="text-sm font-bold text-foreground mb-1">Street</p>
                <Input className="h-11" defaultValue={address.street || ''} placeholder="Street address" onBlur={(e) => handleSaveAddressInline('company', { ...address, street: e.target.value, type: 'company' })} data-testid={`company-address-street-${address.id}`} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-bold text-foreground mb-1">City</p>
                  <Input className="h-11" defaultValue={address.city || ''} placeholder="City" onBlur={(e) => handleSaveAddressInline('company', { ...address, city: e.target.value, type: 'company' })} />
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground mb-1">State</p>
                  <Input className="h-11" defaultValue={address.state || ''} placeholder="State" onBlur={(e) => handleSaveAddressInline('company', { ...address, state: e.target.value, type: 'company' })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-bold text-foreground mb-1">Postal Code</p>
                  <Input className="h-11" defaultValue={address.postal_code || ''} placeholder="Postal code" onBlur={(e) => handleSaveAddressInline('company', { ...address, postal_code: e.target.value, type: 'company' })} />
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground mb-1">Country</p>
                  <Input className="h-11" defaultValue={address.country || ''} placeholder="Country" onBlur={(e) => handleSaveAddressInline('company', { ...address, country: e.target.value, type: 'company' })} />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div>
          <p className="text-sm text-muted-foreground mb-3">No company addresses added</p>
          <Button variant="outline" size="sm" onClick={() => openAddressSheet('company')} data-testid="company-address-add-button-empty"><Plus className="h-4 w-4 mr-2" />Add Company Address</Button>
        </div>
      )}
    </div>
  );

  const renderWorkspace = () => (
    <div data-testid="currency-preferences-card">
      <h3 className="text-lg font-bold text-foreground mb-1">Workspace</h3>
      <p className="text-sm text-muted-foreground mb-6">Currency and locale preferences for your workspace</p>
      <div className="space-y-6">
        <div>
          <p className="text-sm font-bold text-foreground mb-1">Current Currency</p>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold">{CURRENCY_SYMBOLS[user?.currency || 'USD'] || (user?.currency || 'USD')}</span>
            <div>
              <p className="text-sm font-medium">{user?.currency || 'USD'}</p>
              <p className="text-xs text-muted-foreground">{POPULAR_CURRENCIES.find(c => c.code === (user?.currency || 'USD'))?.name || 'US Dollar'}</p>
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground" data-testid="currency-auto-note">Your billing currency is set automatically based on your business region and is used across leads, quotes, invoices, and reports.</p>

        <div className="pt-6 border-t border-border" data-testid="invoice-tax-details-card">
          <p className="text-sm font-bold text-foreground mb-1">Invoice & Tax Details</p>
          <p className="text-xs text-muted-foreground mb-4">Shown on your quotes &amp; invoices. The GSTIN appears on INR tax invoices.</p>
          <div className="space-y-4 max-w-lg">
            <div>
              <Label htmlFor="inv-company-name" className="text-sm">Legal Company Name</Label>
              <Input id="inv-company-name" data-testid="invoice-company-name-input" className="h-11 mt-1"
                value={invoiceTaxForm.company_name}
                onChange={(e) => setInvoiceTaxForm({ ...invoiceTaxForm, company_name: e.target.value })}
                placeholder="e.g. Lockene Pvt Ltd" />
            </div>
            <div>
              <Label htmlFor="inv-gstin" className="text-sm">GSTIN <span className="text-muted-foreground">(optional, India)</span></Label>
              <Input id="inv-gstin" data-testid="invoice-gstin-input" className="h-11 mt-1"
                value={invoiceTaxForm.gstin}
                onChange={(e) => setInvoiceTaxForm({ ...invoiceTaxForm, gstin: e.target.value })}
                placeholder="e.g. 27AADCL2102L1ZQ" maxLength={15} />
            </div>
            <div>
              <Label className="text-sm">Company Logo <span className="text-muted-foreground">(optional, shown on PDFs)</span></Label>
              <div className="flex items-center gap-3 mt-1">
                {invoiceTaxForm.company_logo ? (
                  <img src={invoiceTaxForm.company_logo} alt="Company logo" className="h-12 w-12 object-contain rounded border border-border bg-white p-1" data-testid="invoice-logo-preview" />
                ) : (
                  <div className="h-12 w-12 rounded border border-dashed border-border flex items-center justify-center text-[10px] text-muted-foreground text-center">No logo</div>
                )}
                <input type="file" accept="image/png,image/jpeg" onChange={handleLogoFile} className="hidden" id="company-logo-input" data-testid="invoice-logo-input" />
                <Label htmlFor="company-logo-input" className="cursor-pointer text-sm px-3 py-2 rounded-md border border-border hover:bg-muted transition-colors">Upload</Label>
                {invoiceTaxForm.company_logo && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setInvoiceTaxForm((prev) => ({ ...prev, company_logo: '' }))} data-testid="invoice-logo-remove">Remove</Button>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">PNG or JPEG, under 400KB.</p>
            </div>
            <Button onClick={handleSaveInvoiceTax} disabled={savingInvoiceTax} data-testid="invoice-tax-save-btn">
              {savingInvoiceTax ? 'Saving...' : 'Save Invoice Details'}
            </Button>
            <p className="text-xs text-muted-foreground">Your company address is managed under Organization &rarr; Company Addresses.</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAISettings = () => (
    <div data-testid="ai-settings-card">
      <h3 className="text-lg font-bold text-foreground mb-1">AI Settings</h3>
      <p className="text-sm text-muted-foreground mb-6">Configure AI assistant preferences</p>
      {aiPrefsLoading || !aiPrefs ? (
        <div className="space-y-4">
          <div className="h-10 bg-muted animate-pulse rounded" />
          <div className="h-10 bg-muted animate-pulse rounded" />
          <div className="h-10 bg-muted animate-pulse rounded" />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">AI Suggestions</p>
              <p className="text-xs text-muted-foreground">Enable AI-powered suggestions across the CRM</p>
            </div>
            <Button
              variant={aiPrefs.ai_suggestions_enabled ? 'default' : 'outline'}
              size="sm"
              disabled={aiPrefsSaving}
              onClick={() => saveAIPreference('ai_suggestions_enabled', !aiPrefs.ai_suggestions_enabled)}
              data-testid="toggle-ai-suggestions"
            >
              {aiPrefs.ai_suggestions_enabled ? 'Enabled' : 'Disabled'}
            </Button>
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Auto-Draft Emails</p>
              <p className="text-xs text-muted-foreground">Let AI generate email drafts automatically</p>
            </div>
            <Button
              variant={aiPrefs.ai_auto_draft_emails ? 'default' : 'outline'}
              size="sm"
              disabled={aiPrefsSaving}
              onClick={() => saveAIPreference('ai_auto_draft_emails', !aiPrefs.ai_auto_draft_emails)}
              data-testid="toggle-auto-draft"
            >
              {aiPrefs.ai_auto_draft_emails ? 'Enabled' : 'Disabled'}
            </Button>
          </div>
          <Separator />
          <div className="space-y-2">
            <p className="text-sm font-medium">AI Tone</p>
            <p className="text-xs text-muted-foreground">Choose the tone for AI-generated content</p>
            <Select value={aiPrefs.ai_tone} onValueChange={(v) => saveAIPreference('ai_tone', v)}>
              <SelectTrigger data-testid="ai-tone-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="professional">Professional</SelectItem>
                <SelectItem value="friendly">Friendly</SelectItem>
                <SelectItem value="concise">Concise</SelectItem>
                <SelectItem value="formal">Formal</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium">Response Length</p>
            <p className="text-xs text-muted-foreground">Preferred length for AI responses</p>
            <Select value={aiPrefs.ai_response_length} onValueChange={(v) => saveAIPreference('ai_response_length', v)}>
              <SelectTrigger data-testid="ai-response-length-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="brief">Brief</SelectItem>
                <SelectItem value="balanced">Balanced</SelectItem>
                <SelectItem value="detailed">Detailed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium">AI Language</p>
            <p className="text-xs text-muted-foreground">Language for AI-generated content</p>
            <Select value={aiPrefs.ai_language} onValueChange={(v) => saveAIPreference('ai_language', v)}>
              <SelectTrigger data-testid="ai-language-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="english">English</SelectItem>
                <SelectItem value="spanish">Spanish</SelectItem>
                <SelectItem value="french">French</SelectItem>
                <SelectItem value="german">German</SelectItem>
                <SelectItem value="hindi">Hindi</SelectItem>
                <SelectItem value="arabic">Arabic</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </div>
  );

  const renderPrivacyData = () => {
    const isDeletionPending = privacyStatus?.deletion_requested;
    const purgeDate = privacyStatus?.deletion_purge_at ? new Date(privacyStatus.deletion_purge_at) : null;

    return (
      <div className="space-y-8" data-testid="privacy-data-section">
        <div>
          <h3 className="text-lg font-bold text-foreground mb-1">Privacy & Data</h3>
          <p className="text-sm text-muted-foreground mb-6">Manage your privacy preferences and personal data</p>
        </div>

        {/* Privacy Policy Acceptance */}
        <div className="rounded-lg border p-5" data-testid="privacy-acceptance-card">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center shrink-0", privacyStatus?.privacy_accepted ? "bg-emerald-500/10" : "bg-amber-500/10")}>
                {privacyStatus?.privacy_accepted ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <FileText className="h-5 w-5 text-amber-600" />}
              </div>
              <div>
                <p className="text-sm font-semibold">Privacy Policy & Terms of Service</p>
                {privacyStatus?.privacy_accepted ? (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Accepted on {new Date(privacyStatus.privacy_accepted_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} (v{privacyStatus.privacy_version})
                  </p>
                ) : (
                  <p className="text-xs text-amber-600 mt-0.5">Not yet accepted</p>
                )}
                <div className="flex items-center gap-3 mt-2">
                  <a href="https://www.lockene.ai/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1">
                    <ExternalLink className="h-3 w-3" />Privacy Policy
                  </a>
                  <a href="https://www.lockene.ai/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1">
                    <ExternalLink className="h-3 w-3" />Terms & Conditions
                  </a>
                </div>
              </div>
            </div>
            {!privacyStatus?.privacy_accepted && (
              <Button size="sm" onClick={handleAcceptPrivacy} data-testid="accept-privacy-btn">
                Accept
              </Button>
            )}
          </div>
        </div>

        <Separator />

        {/* Marketing Consent */}
        <div data-testid="marketing-consent-card">
          <div className="flex items-start gap-3 mb-4">
            <div className="h-9 w-9 rounded-lg bg-violet-500/10 flex items-center justify-center shrink-0">
              <BellRing className="h-5 w-5 text-violet-600" />
            </div>
            <div>
              <p className="text-sm font-semibold">Email Preferences</p>
              <p className="text-xs text-muted-foreground mt-0.5">Control which emails you receive from Lockene</p>
            </div>
          </div>
          <div className="ml-12 space-y-3">
            {[
              { key: 'marketing_emails', label: 'Marketing & promotional emails', desc: 'Product announcements, offers, and newsletters' },
              { key: 'product_updates', label: 'Product updates', desc: 'New features, improvements, and changelog' },
              { key: 'ai_insights_emails', label: 'AI insights & digests', desc: 'Weekly AI-generated reports and recommendations' },
            ].map(({ key, label, desc }) => (
              <div key={key} className="flex items-center justify-between py-1.5">
                <div>
                  <p className="text-sm">{label}</p>
                  <p className="text-xs text-muted-foreground">{desc}</p>
                </div>
                <Switch
                  checked={privacyStatus?.marketing_consent?.[key] ?? false}
                  onCheckedChange={(val) => handleMarketingToggle(key, val)}
                  data-testid={`marketing-toggle-${key}`}
                />
              </div>
            ))}
          </div>
        </div>

        <Separator />

        {/* Export Data */}
        <div data-testid="export-data-card">
          <div className="flex items-start gap-3 mb-4">
            <div className="h-9 w-9 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
              <Download className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-semibold">Download Your Data</p>
              <p className="text-xs text-muted-foreground mt-0.5">Export all your personal data stored in Lockene (GDPR Article 20)</p>
            </div>
          </div>
          <div className="flex gap-3 ml-12">
            <Button
              variant="outline"
              size="sm"
              disabled={exportingJson}
              onClick={handleExportJson}
              data-testid="export-json-btn"
            >
              {exportingJson ? <div className="h-3.5 w-3.5 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" /> : <Download className="h-3.5 w-3.5 mr-2" />}
              JSON
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={exportingCsv}
              onClick={handleExportCsv}
              data-testid="export-csv-btn"
            >
              {exportingCsv ? <div className="h-3.5 w-3.5 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" /> : <Download className="h-3.5 w-3.5 mr-2" />}
              CSV
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground ml-12 mt-2">
            Includes: profile, leads, deals, activities, tickets, invoices, quotes, and AI data
          </p>
        </div>

        <Separator />

        {/* Delete Account */}
        <div data-testid="delete-account-card">
          <div className="flex items-start gap-3 mb-4">
            <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center shrink-0", isDeletionPending ? "bg-amber-500/10" : "bg-red-500/10")}>
              {isDeletionPending ? <AlertTriangle className="h-5 w-5 text-amber-600" /> : <Trash2 className="h-5 w-5 text-red-600" />}
            </div>
            <div>
              <p className="text-sm font-semibold">{isDeletionPending ? 'Account Deletion Scheduled' : 'Delete Account'}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {isDeletionPending
                  ? `Your account will be permanently deleted on ${purgeDate?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`
                  : 'Permanently delete your account and all associated data. 30-day grace period applies.'
                }
              </p>
            </div>
          </div>

          {isDeletionPending ? (
            <div className="ml-12 space-y-3">
              <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 p-3">
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  You can cancel this request anytime before {purgeDate?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}. After that date, all your data will be permanently removed and cannot be recovered.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancelDeletion}
                data-testid="cancel-deletion-btn"
              >
                <XCircle className="h-3.5 w-3.5 mr-2" />
                Cancel Deletion
              </Button>
            </div>
          ) : (
            <div className="ml-12">
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setDeleteAccountDialog(true)}
                data-testid="delete-account-btn"
              >
                <Trash2 className="h-3.5 w-3.5 mr-2" />
                Delete My Account
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderEmbeddedPage = (Component) => () => <Component />;

  const SECTION_RENDERERS = {
    'my-profile': renderMyProfile,
    'password-security': renderPasswordSecurity,
    'company-details': renderCompanyDetails,
    'company-addresses': renderCompanyAddresses,
    'workspace': renderWorkspace,
    'ai-settings': renderAISettings,
    'privacy-data': renderPrivacyData,
    'users': renderEmbeddedPage(UsersPage),
    'products': renderEmbeddedPage(ProductsCatalog),
    'data-import': renderEmbeddedPage(DataImport),
  };

  // ---------- Render ----------
  return (
    <div className="h-[calc(100vh-140px)] flex -mx-4 lg:-mx-6 -my-4 lg:-my-6" data-testid="profile-page">
      {/* Left Sidebar */}
      <div className="w-56 shrink-0 border-r border-border px-5 py-6 sticky top-0 self-start h-full bg-background overflow-y-auto" data-testid="profile-sidebar">
        <h1 className="text-2xl font-semibold tracking-tight mb-5">Settings</h1>
        <nav className="space-y-5">
          {SIDEBAR_SECTIONS.map((section) => (
            <div key={section.category}>
              <p className="text-sm font-bold text-foreground mb-2">
                {section.category}
              </p>
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const isActive = activeSection === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => { setActiveSection(item.id); setSearchParams({ tab: item.id }); }}
                      className={cn(
                        'w-full text-left text-sm py-1.5 px-2 rounded-md transition-all duration-150',
                        isActive
                          ? 'text-primary font-medium bg-primary/5'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      )}
                      data-testid={`sidebar-${item.id}`}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </div>

      {/* Right Content */}
      <div className={`flex-1 overflow-y-auto ${['users', 'products', 'data-import'].includes(activeSection) ? 'px-4 py-4' : 'px-10 py-8'}`} data-testid="profile-content">
        {SECTION_RENDERERS[activeSection]?.()}
      </div>

      {/* Address Sheet */}
      <Sheet open={addressSheet.open} onOpenChange={closeAddressSheet}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{addressSheet.data ? 'Edit' : 'Add'} {addressSheet.type === 'personal' ? 'Personal' : 'Company'} Address</SheetTitle>
            <SheetDescription>{addressSheet.type === 'personal' ? 'Your home or personal address' : 'Company office, warehouse, or business address'}</SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            {addressSheet.type === 'company' && (
              <div><Label>Label *</Label><Input value={addressForm.label} onChange={(e) => setAddressForm({ ...addressForm, label: e.target.value })} placeholder="e.g., Headquarters" data-testid="address-label-input" /></div>
            )}
            <div><Label>Street *</Label><Input value={addressForm.street} onChange={(e) => setAddressForm({ ...addressForm, street: e.target.value })} data-testid="address-street-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>City *</Label><Input value={addressForm.city} onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })} data-testid="address-city-input" /></div>
              <div><Label>State *</Label><Input value={addressForm.state} onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })} data-testid="address-state-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Postal Code *</Label><Input value={addressForm.postal_code} onChange={(e) => setAddressForm({ ...addressForm, postal_code: e.target.value })} data-testid="address-postal-input" /></div>
              <div><Label>Country *</Label><Input value={addressForm.country} onChange={(e) => setAddressForm({ ...addressForm, country: e.target.value })} data-testid="address-country-input" /></div>
            </div>
            <div><Label>Landmark (Optional)</Label><Input value={addressForm.landmark} onChange={(e) => setAddressForm({ ...addressForm, landmark: e.target.value })} data-testid="address-landmark-input" /></div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="ghost" onClick={closeAddressSheet} data-testid="address-cancel-button">Cancel</Button>
              <Button onClick={handleSaveAddress} data-testid="address-save-button">Save Address</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Password Change Sheet */}
      <Sheet open={passwordSheet} onOpenChange={setPasswordSheet}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Change Password</SheetTitle>
            <SheetDescription>Update your password to keep your account secure</SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            <div><Label>Current Password *</Label><Input type="password" value={passwordForm.current_password} onChange={(e) => handlePasswordChange('current_password', e.target.value)} data-testid="current-password-input" /></div>
            <div>
              <Label>New Password *</Label>
              <Input type="password" value={passwordForm.new_password} onChange={(e) => handlePasswordChange('new_password', e.target.value)} data-testid="new-password-input" />
              {passwordForm.new_password && (
                <div className="mt-2 space-y-1" data-testid="password-strength-meter">
                  <Progress value={[4, 25, 50, 75, 100][passwordStrength.score]} className="h-1.5" />
                  <div className="text-xs font-medium" style={{ color: getPasswordStrengthColor(passwordStrength.score) }}>{getPasswordStrengthLabel(passwordStrength.score)}</div>
                  {passwordStrength.feedback.warning && <div className="text-xs text-destructive">{passwordStrength.feedback.warning}</div>}
                </div>
              )}
            </div>
            <div>
              <Label>Confirm New Password *</Label>
              <Input type="password" value={passwordForm.confirm_password} onChange={(e) => handlePasswordChange('confirm_password', e.target.value)} data-testid="confirm-password-input" />
              {passwordError && <div className="text-xs text-destructive mt-1">{passwordError}</div>}
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="ghost" onClick={() => { setPasswordSheet(false); setPasswordForm({ current_password: '', new_password: '', confirm_password: '' }); setPasswordStrength({ score: 0, feedback: {} }); setPasswordError(''); }} data-testid="password-change-cancel-button">Cancel</Button>
              <Button onClick={handleChangePassword} disabled={!passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password} data-testid="password-change-save-button">Change Password</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteDialog.type === 'personal' ? 'Personal' : 'Company'} Address?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-address-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAddress} data-testid="delete-address-confirm-button">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Account Confirmation Dialog */}
      <AlertDialog open={deleteAccountDialog} onOpenChange={setDeleteAccountDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive" data-testid="delete-account-dialog-title">
              <AlertTriangle className="h-5 w-5" />
              Delete Your Account
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 pt-2">
                <p className="text-sm">This will schedule your account for permanent deletion after <strong>30 days</strong>. During the grace period you can cancel anytime.</p>
                <p className="text-sm text-destructive font-medium">After 30 days, all your data (profile, leads, deals, activities, invoices, AI memory) will be permanently and irreversibly removed.</p>
                <div className="space-y-2 pt-2">
                  <label className="text-xs font-medium text-foreground">Confirm your password</label>
                  <Input
                    type="password"
                    placeholder="Enter your password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    data-testid="delete-account-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-foreground">Reason for leaving (optional)</label>
                  <Input
                    placeholder="Help us improve..."
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    data-testid="delete-account-reason-input"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-account-cancel-dialog-btn" onClick={() => { setDeletePassword(''); setDeleteReason(''); }}>
              Cancel
            </AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={!deletePassword || deletingAccount}
              onClick={handleRequestDeletion}
              data-testid="delete-account-confirm-btn"
            >
              {deletingAccount ? <div className="h-3.5 w-3.5 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" /> : null}
              {deletingAccount ? 'Processing...' : 'Delete My Account'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
